import { useRef, useState, useEffect } from 'react';
import { MS_IN_DAY } from '@CS/utils/Number';
import { useTranslation } from '@CS/hooks';
import CSS from './Timer.module.css';
import Content from './content';

type TimerProps = {
  // --- Props for `countup === false | undefined` ---
  // `new Date().toISOString()` | `Date.now()`:
  expiration?: string | number;
  onComplete?: () => void;

  // --- Props for `countup === true` ---
  // `new Date().getTime()` | `Date.now()`:
  startTime?: number;
  countup?: boolean;

  // --- Common props ---
  begin?: boolean;
};

export const Timer = ({
  onComplete,
  expiration,
  startTime,
  countup,
  begin
}: TimerProps) => {
  const [time, setTime] = useState((countup && '00:00') || '');
  const { daysLeft } = useTranslation(Content);
  const date = useRef(expiration);

  const start = useRef(0);
  const end = useRef(0);
  const raf = useRef(0);

  const tick = (time: DOMHighResTimeStamp) => {
    time = countup ? time - start.current : end.current - Date.now();

    let seconds = Math.max(time / 1e3, 0);
    let minutes = seconds / 60;
    let hours = minutes / 60;
    let days = (hours / 24) | 0;

    seconds = seconds % 60 | 0;
    minutes = minutes % 60 | 0;
    hours = hours % 24 | 0;

    const dd = `${(days < 10 && '0') || ''}${days}`;
    const hh = `${(hours < 10 && '0') || ''}${hours}`;
    const mm = `${(minutes < 10 && '0') || ''}${minutes}`;
    const ss = `${(seconds < 10 && '0') || ''}${seconds}`;

    raf.current = requestAnimationFrame(tick);

    if (!countup) {
      setTime(`${(hours && `${hh}:`) || ''}${mm}:${ss}`);
      !hours && !minutes && !seconds &&
        ~cancelAnimationFrame(raf.current) && onComplete?.();
    } else
      setTime(
        `${(days && `${dd}:`) || ''}${((days || hours) && `${hh}:`) || ''}${mm}:${ss}`
      );
  };

  const onVisibilityChange = () =>
    document.visibilityState === 'visible' &&
      !countup && ['00:00:00', '00:00'].includes(time) &&
      ~cancelAnimationFrame(raf.current) && onComplete?.();

  useEffect(() => {
    const endTime = new Date(expiration!).getTime();
    const left = endTime - Date.now();
    end.current = endTime;

    if (left > MS_IN_DAY) {
      const days = Math.ceil(left / MS_IN_DAY);
      return setTime(`${days} ${daysLeft}`);
    }

    if (!begin) {
      !raf.current && tick(0);
      cancelAnimationFrame(raf.current);
      return;
    }

    raf.current = requestAnimationFrame(tick);

    if (!startTime) return;
    start.current -= Date.now() - startTime;
  }, [begin]);

  useEffect(() => {
    document.addEventListener('visibilitychange', onVisibilityChange);
    start.current = performance.now();

    return () => {
      cancelAnimationFrame(raf.current);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  return (
    <time dateTime={`${date.current}`} className={CSS.container}>
      {time}
    </time>
  );
};
